/**
 * FwmShell — the one header, ground and footer every public FWM section wears.
 * The homepage is the source of truth: cream ground, near-black ink, gold
 * accent, Newsreader for display type. Events, Churches and Giving import the
 * same pieces so a visitor never feels they have left the platform.
 */
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { Menu, X, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NAV = [
  ['/events', 'Events'],
  ['/find-church', 'Find a church'],
  ['/churches', 'Churches'],
  ['/affiliated-ministries', 'Affiliates'],
  ['/giving', 'Giving'],
  ['/media', 'Media'],
  ['/shop', 'Shop'],
];

export const useFwmBrand = () => {
  const { data } = useQuery({
    queryKey: ['app-config'],
    queryFn: async () => { try { return (await api.get('/app-config')).data; } catch { return null; } },
    staleTime: 300000,
  });
  const raw = data?.logo_url;
  return {
    ready: data !== undefined,
    logoUrl: raw ? (raw.startsWith('http') ? raw : `${API_URL}${raw}`) : null,
    brandName: data?.app_name || 'Faith World Ministries',
    tagline: data?.tagline || '',
  };
};

export const FwmCrest = ({ className = 'h-14' }) => {
  const { logoUrl, brandName, ready } = useFwmBrand();
  const [broken, setBroken] = React.useState(false);
  // `/api/app-logo` is a stable redirect to whatever crest is configured and is
  // preloaded in index.html, so the real logo paints on the first frame
  // instead of a placeholder while /app-config resolves.
  const src = logoUrl || '/api/app-logo';
  return (
    <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="fwm-crest">
      {broken ? (
        <span className={`${className} w-full max-w-[120px] ${ready ? '' : 'animate-pulse bg-[color:var(--fwm-line)]'}`} />
      ) : (
        <img src={src} alt={brandName} fetchPriority="high" onError={() => setBroken(true)}
             className={`${className} w-auto max-w-[230px] object-contain`} />
      )}
      {broken && ready && (
        <span className="fwm-display text-base font-semibold tracking-tight truncate">{brandName}</span>
      )}
    </Link>
  );
};

export const FwmHeader = ({ ctaLabel = 'Explore events', ctaLink = '/events',
                           overDark = false }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    if (!overDark) return undefined;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [overDark]);
  const onPhoto = overDark && !scrolled;
  return (
    <header className={`fixed top-0 inset-x-0 z-40 border-b transition-colors duration-500
                        ${onPhoto ? 'fwm-header-ondark'
                                  : 'border-[color:var(--fwm-line)] backdrop-blur-xl'}`}
            style={onPhoto ? undefined : { backgroundColor: 'var(--fwm-nav)' }}
            data-testid="fwm-header">
      <div className="mx-auto max-w-[1180px] px-6 lg:px-10 h-[84px] flex items-center gap-8">
        <FwmCrest className="h-14" />
        <nav className="hidden lg:flex items-center gap-8 ml-auto text-sm" data-testid="fwm-nav">
          {NAV.map(([href, label]) => (
            <Link key={href} to={href} data-testid={`fwm-nav-${label.toLowerCase()}`}
                  data-active={pathname.startsWith(href)}
                  className={`fwm-nav-link transition-colors hover:text-[color:var(--fwm-ink)] ${
                    pathname.startsWith(href) ? 'text-[color:var(--fwm-ink)] font-medium'
                                              : 'text-[color:var(--fwm-muted)]'}`}>
              {label}
            </Link>
          ))}
          <Link to="/login" data-testid="fwm-nav-staff"
                className="fwm-nav-link text-[color:var(--fwm-muted)] hover:text-[color:var(--fwm-ink)] transition-colors">
            Staff Sign In
          </Link>
        </nav>
        <div className="flex items-center gap-2 ml-auto lg:ml-0">
          <button onClick={() => navigate(ctaLink)} data-testid="fwm-header-cta"
                  className={`hidden sm:inline-flex ${onPhoto ? 'fwm-btn-ondark !py-2.5 !px-5 !text-[0.8125rem]'
                                                              : 'fwm-btn'}`}>
            {ctaLabel} <ArrowRight className="h-4 w-4" />
          </button>
          <button className={`lg:hidden p-2 ${onPhoto ? 'text-[#F6F1E4]'
                                                       : 'text-[color:var(--fwm-body)]'}`}
                  aria-label="Menu"
                  onClick={() => setOpen((v) => !v)} data-testid="fwm-nav-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[color:var(--fwm-line)] px-6 py-4 grid gap-3"
             style={{ backgroundColor: 'var(--fwm-nav)' }}
             data-testid="fwm-nav-mobile">
          {NAV.map(([href, label]) => (
            <Link key={href} to={href} onClick={() => setOpen(false)}
                  className="text-sm text-[color:var(--fwm-body)]">{label}</Link>
          ))}
          <Link to="/affiliated-ministries/apply" onClick={() => setOpen(false)}
                data-testid="fwm-nav-mobile-affiliate-apply"
                className="text-sm text-[color:var(--fwm-body)]">Apply for Affiliation</Link>
          <Link to="/login" onClick={() => setOpen(false)}
                className="text-sm font-semibold text-[color:var(--fwm-accent)]">Staff Sign In</Link>
        </div>
      )}
    </header>
  );
};

export const FwmFooter = () => {
  const { logoUrl, brandName, tagline } = useFwmBrand();
  return (
    <footer className="fwm-footer-dark mt-auto px-6 lg:px-10 py-16" data-testid="fwm-footer">
      <div className="mx-auto max-w-[1180px] grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 min-w-0">
          <img src={logoUrl || '/api/app-logo'} alt={brandName}
               className="h-12 w-auto max-w-[200px] object-contain brightness-0 invert"
               data-testid="fwm-footer-logo" />
          <p className="mt-5 max-w-sm text-sm leading-relaxed">
            {tagline || 'One family of churches — gathering, giving and growing together.'}
          </p>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8 text-sm">
          {[['Gather', [['/events', 'Events'], ['/churches', 'Churches'],
                        ['/affiliated-ministries', 'Affiliated Ministries'],
                        ['/affiliated-ministries/apply', 'Apply for Affiliation'],
                        ['/tv', 'Faith Works TV']]],
            ['Grow', [['/media', 'Media'], ['/bible-home', 'Bible'], ['/shop', 'Ministry Shop']]],
            ['Give', [['/giving', 'Giving'], ['/campaigns', 'Campaigns'], ['/login', 'Staff Sign In']]]]
            .map(([heading, links]) => (
              <div key={heading}>
                <p className="text-[0.625rem] font-semibold uppercase tracking-[0.2em]
                              text-[color:var(--fwm-accent-soft)]">{heading}</p>
                <ul className="mt-4 space-y-2.5">
                  {links.map(([href, label]) => (
                    <li key={href}>
                      <Link to={href}
                            data-testid={href === '/login' ? 'fwm-footer-staff' : undefined}
                            className="hover:text-[#FAF5E8] transition-colors">{label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      </div>
      <div className="mx-auto max-w-[1180px] mt-14 pt-6 border-t border-white/10
                      flex flex-wrap gap-4 justify-between text-xs">
        <p>© {new Date().getFullYear()} {brandName}</p>
        <div className="flex gap-6">
          <Link to="/privacy" className="hover:text-[#FAF5E8] transition-colors">Privacy</Link>
          <Link to="/cookies" className="hover:text-[#FAF5E8] transition-colors">Cookies</Link>
        </div>
      </div>
    </footer>
  );
};

const FwmShell = ({ children, ctaLabel, ctaLink, testId = 'fwm-shell',
                    overDark = false }) => (
  <div className="fwm-page min-h-screen flex flex-col" data-testid={testId}>
    <FwmHeader ctaLabel={ctaLabel} ctaLink={ctaLink} overDark={overDark} />
    <main className={`flex-1 ${overDark ? '' : 'pt-[84px]'}`}>{children}</main>
    <FwmFooter />
  </div>
);

export default FwmShell;
